// ---
// EcoDrivesMart Compare controller
// assets/eds-compare.js
//
// One controller for the whole feature:
//   - the compare set, persisted to localStorage and synced across tabs
//   - every toggle button on the site (data-eds-compare-toggle)
//   - the persistent tray (sections/eds-compare-tray.liquid)
//   - the in-page compare drawer, opened from the tray Compare button
//   - the compare page table, when Compare opens is set to page mode
//
// The table is assembled the same way in the drawer and on the page: one
// column section is fetched per selected drive through the Section
// Rendering API, then the header cell and value cells are slotted into the
// table by their data-row key.
//
// Config (limit, view, labels, paths) is read from the JSON the tray
// section prints. Everything degrades to sane defaults if the tray has not
// been added to a section group yet.
// ---

(() => {
  if (window.EDSCompareLoaded) return;
  window.EDSCompareLoaded = true;

  var STORAGE_KEY = 'eds:compare:v1';
  var CHANGE_EVENT = 'eds:compare:change';

  // --- Config -------------------------------------------------------------

  var readConfig = function () {
    var fallback = {
      limit: 3,
      compareView: 'drawer',
      comparePath: '/pages/compare',
      columnSection: 'eds-compare-column',
      endpointBase: '/products/',
      labels: {}
    };
    var node = document.getElementById('eds-compare-config');
    if (!node) return fallback;
    try {
      var parsed = JSON.parse(node.textContent || '{}');
      return Object.assign(fallback, parsed, {
        labels: Object.assign({}, fallback.labels, parsed.labels || {})
      });
    } catch (e) {
      return fallback;
    }
  };

  var config = readConfig();
  var LIMIT = Number(config.limit) > 0 ? Number(config.limit) : 3;
  var VIEW = config.compareView === 'page' ? 'page' : 'drawer';

  var L = Object.assign(
    {
      limitMessage: 'You can compare up to ' + LIMIT + ' drives. Remove one to add another.',
      addLabel: 'Add a drive',
      addHint: 'Pick more from any product page'
    },
    config.labels || {}
  );
  if (!L.limitMessage) {
    L.limitMessage = 'You can compare up to ' + LIMIT + ' drives. Remove one to add another.';
  }
  if (!L.addLabel) { L.addLabel = 'Add a drive'; }
  if (!L.addHint) { L.addHint = 'Pick more from any product page'; }

  // Whether the matrix is filtered to differing rows only. Off by default.
  var diffOnly = false;

  // --- Store --------------------------------------------------------------

  var load = function () {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (it) {
        return it && typeof it.handle === 'string' && it.handle.length;
      });
    } catch (e) {
      return [];
    }
  };

  var persist = function (next) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      // storage unavailable (private mode quota etc.), keep in memory only
    }
  };

  var items = load();

  var emitChange = function (detail) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: detail || {} }));
  };

  var has = function (handle) {
    return items.some(function (it) {
      return it.handle === handle;
    });
  };

  var isFull = function () {
    return items.length >= LIMIT;
  };

  var add = function (item) {
    if (!item || !item.handle) return { ok: false, reason: 'invalid' };
    if (has(item.handle)) return { ok: false, reason: 'dupe' };
    if (isFull()) return { ok: false, reason: 'full' };
    items = items.concat([
      {
        handle: item.handle,
        title: item.title || item.handle,
        image: item.image || ''
      }
    ]);
    persist(items);
    emitChange({ type: 'add', handle: item.handle });
    return { ok: true };
  };

  var remove = function (handle) {
    if (!has(handle)) return;
    items = items.filter(function (it) {
      return it.handle !== handle;
    });
    persist(items);
    emitChange({ type: 'remove', handle: handle });
  };

  var clear = function () {
    if (!items.length) return;
    items = [];
    persist(items);
    emitChange({ type: 'clear' });
  };

  // --- Buttons ------------------------------------------------------------

  var buttons = new Set();

  var labelFor = function (btn, active) {
    return active
      ? btn.dataset.edsCompareLabelRemove || 'Remove from compare'
      : btn.dataset.edsCompareLabelAdd || 'Add to compare';
  };

  var syncButton = function (btn) {
    var active = has(btn.dataset.edsCompareHandle);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.classList.toggle('is-active', active);
    var labelNode = btn.querySelector('[data-eds-compare-btn-label]');
    if (labelNode) labelNode.textContent = labelFor(btn, active);
  };

  var syncButtons = function () {
    buttons.forEach(syncButton);
  };

  var onButtonClick = function (event) {
    var btn = event.currentTarget;
    var handle = btn.dataset.edsCompareHandle;
    if (!handle) return;

    if (!has(handle) && isFull()) {
      showMessage(L.limitMessage);
      return;
    }
    if (has(handle)) {
      remove(handle);
    } else {
      add({
        handle: handle,
        title: btn.dataset.edsCompareTitle,
        image: btn.dataset.edsCompareImage
      });
    }
  };

  var bindButtons = function (scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var list = root.querySelectorAll('[data-eds-compare-toggle]');
    list.forEach(function (btn) {
      if (buttons.has(btn)) return;
      buttons.add(btn);
      btn.addEventListener('click', onButtonClick);
      syncButton(btn);
    });
  };

  // --- Tray ---------------------------------------------------------------

  var trayBound = false;

  var trayEls = function () {
    var tray = document.querySelector('[data-eds-compare-tray]');
    if (!tray) return null;
    return {
      tray: tray,
      list: tray.querySelector('[data-eds-tray-items]'),
      count: tray.querySelector('[data-eds-tray-count]'),
      clearBtn: tray.querySelector('[data-eds-tray-clear]'),
      cta: tray.querySelector('[data-eds-tray-compare]'),
      msg: tray.querySelector('[data-eds-tray-msg]')
    };
  };

  var buildTrayItem = function (it) {
    var cell = document.createElement('div');
    cell.className = 'eds-cmp-tray__item';
    cell.setAttribute('data-handle', it.handle);

    if (it.image) {
      var img = document.createElement('img');
      img.className = 'eds-cmp-tray__thumb';
      img.src = it.image;
      img.alt = it.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      cell.appendChild(img);
    }

    var title = document.createElement('span');
    title.className = 'eds-cmp-tray__title';
    title.textContent = it.title || it.handle;
    cell.appendChild(title);

    var rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'eds-cmp-tray__remove';
    rm.setAttribute('data-handle', it.handle);
    rm.setAttribute('aria-label', 'Remove ' + (it.title || it.handle) + ' from compare');
    rm.textContent = '\u2715';
    rm.addEventListener('click', function () {
      remove(it.handle);
    });
    cell.appendChild(rm);

    return cell;
  };

  var updateCounts = function () {
    document.querySelectorAll('[data-eds-tray-count], [data-eds-drawer-count]').forEach(function (node) {
      node.textContent = String(items.length);
    });
    document.querySelectorAll('[data-eds-cmp-corner-count]').forEach(function (node) {
      node.textContent = items.length + ' of ' + LIMIT;
    });
  };

  var renderTray = function () {
    updateCounts();
    setColsVar();

    var els = trayEls();
    if (!els) return;

    var visible = items.length > 0;
    els.tray.toggleAttribute('hidden', !visible);
    els.tray.classList.toggle('is-visible', visible);

    if (els.cta) {
      els.cta.setAttribute('aria-disabled', visible ? 'false' : 'true');
      if (VIEW === 'page' && config.comparePath) els.cta.setAttribute('href', config.comparePath);
    }

    if (els.list) {
      els.list.textContent = '';
      items.forEach(function (it) {
        els.list.appendChild(buildTrayItem(it));
      });
    }

    if (!trayBound) {
      trayBound = true;
      if (els.clearBtn) {
        els.clearBtn.addEventListener('click', function () {
          clear();
        });
      }
      if (els.cta) {
        els.cta.addEventListener('click', function (event) {
          if (!items.length) {
            event.preventDefault();
            return;
          }
          if (VIEW === 'drawer') {
            event.preventDefault();
            openDrawer();
          }
          // page mode lets the anchor navigate to the compare page
        });
      }
    }
  };

  var msgTimer = null;

  var showMessage = function (text) {
    if (!text) return;
    var els = trayEls();

    if (els && els.msg) {
      els.msg.textContent = text;
      els.msg.hidden = false;
      els.tray.toggleAttribute('hidden', false);
      els.tray.classList.add('is-visible');
      window.clearTimeout(msgTimer);
      msgTimer = window.setTimeout(function () {
        els.msg.hidden = true;
        if (!items.length) {
          els.tray.toggleAttribute('hidden', true);
          els.tray.classList.remove('is-visible');
        }
      }, 4000);
      return;
    }

    var toast = document.querySelector('[data-eds-compare-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'eds-cmp-toast';
      toast.setAttribute('data-eds-compare-toast', '');
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = text;
    toast.classList.add('is-visible');
    window.clearTimeout(msgTimer);
    msgTimer = window.setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 4000);
  };

  // --- Table builder (shared by the drawer and the compare page) ----------

  var isInDrawer = function (root) {
    return !!root.closest('[data-eds-compare-drawer]');
  };

  var pageRoots = function () {
    return Array.from(document.querySelectorAll('[data-eds-compare-root]')).filter(function (root) {
      return !isInDrawer(root);
    });
  };

  var drawerEl = function () {
    return document.querySelector('[data-eds-compare-drawer]');
  };

  var drawerRoot = function () {
    var d = drawerEl();
    return d ? d.querySelector('[data-eds-compare-root]') : null;
  };

  var drawerIsOpen = function () {
    var d = drawerEl();
    return !!(d && d.open);
  };

  // Panel width tracks the live column count: tight for one drive, wider for
  // three, with no empty rail. The desktop CSS reads --eds-cmp-cols.
  var setColsVar = function () {
    var d = drawerEl();
    if (d) d.style.setProperty('--eds-cmp-cols', String(Math.max(1, items.length)));
  };

  var setTableState = function (root, hasItems) {
    var scroll = root.querySelector('[data-eds-cmp-scroll]');
    var empty = root.querySelector('[data-eds-cmp-empty]');
    if (scroll) scroll.hidden = !hasItems;
    if (empty) empty.hidden = hasItems;
    root.classList.toggle('is-single', items.length === 1);
  };

  var clearInjected = function (root) {
    root.querySelectorAll('[data-eds-cmp-injected]').forEach(function (node) {
      node.remove();
    });
  };

  var bindColumnRemovers = function (root) {
    root.querySelectorAll('[data-eds-cmp-col-remove]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function () {
        remove(btn.dataset.handle);
      });
    });
  };

  var fetchColumn = function (root, handle) {
    var base = root.dataset.cmpEndpoint || config.endpointBase || '/products/';
    var sectionId = root.dataset.cmpSection || config.columnSection || 'eds-compare-column';
    var url = base + encodeURIComponent(handle) + '?section_id=' + encodeURIComponent(sectionId);
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (res) {
        return res.ok ? res.text() : Promise.reject(res.status);
      })
      .then(function (html) {
        return { handle: handle, html: html };
      })
      .catch(function () {
        return { handle: handle, html: null };
      });
  };

  var escapeHtml = function (str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  };

  // A header cell for an empty slot, so a partial set still reads as a full
  // matrix instead of a lone column. Drives are added from product pages.
  var buildAddSlotHead = function () {
    var th = document.createElement('th');
    th.className = 'eds-cmp__headcell eds-cmp__headcell--slot';
    th.setAttribute('data-eds-cmp-injected', '');
    th.setAttribute('aria-hidden', 'true');
    th.innerHTML =
      '<div class="eds-cmp__addslot">' +
        '<span class="eds-cmp__addslot-plus" aria-hidden="true">+</span>' +
        '<span class="eds-cmp__addslot-label">' + escapeHtml(L.addLabel) + '</span>' +
        '<span class="eds-cmp__addslot-hint">' + escapeHtml(L.addHint) + '</span>' +
      '</div>';
    return th;
  };

  var buildEmptyCell = function () {
    var td = document.createElement('td');
    td.className = 'eds-cmp__cell eds-cmp__cell--slot';
    td.setAttribute('data-eds-cmp-injected', '');
    td.setAttribute('aria-hidden', 'true');
    td.innerHTML = '<span class="eds-cmp__val eds-cmp__val--slot">&mdash;</span>';
    return td;
  };

  // Pad the header and every spec row out to the limit with slot cells.
  var fillEmptySlots = function (root, headRow, tbody, filled) {
    var slots = LIMIT - filled;
    if (slots <= 0) return;
    for (var i = 0; i < slots; i++) {
      if (headRow) headRow.appendChild(buildAddSlotHead());
      if (tbody) {
        tbody.querySelectorAll('tr[data-row]').forEach(function (tr) {
          tr.appendChild(buildEmptyCell());
        });
      }
    }
  };

  // The comparable text of a cell is its full value, model-number subline
  // included, so two drives in the same series but different models still
  // read as a difference.
  var cellCompareText = function (cell) {
    if (!cell) return '';
    return (cell.textContent || '').replace(/\s+/g, ' ').trim();
  };

  // Flag a row when the drives in it do not all share the same value. Only
  // real drive cells count, never the empty slots.
  var computeDiffs = function (root) {
    var tbody = root.querySelector('[data-eds-cmp-body]');
    if (!tbody) return;
    tbody.querySelectorAll('tr[data-row]').forEach(function (tr) {
      var cells = tr.querySelectorAll('[data-eds-cmp-cell]');
      var differs = false;
      var first = null;
      var seen = 0;
      cells.forEach(function (c) {
        var val = cellCompareText(c);
        if (seen === 0) { first = val; } else if (val !== first) { differs = true; }
        seen++;
      });
      tr.classList.toggle('is-diff', differs && seen >= 2);
    });
  };

  // Apply the current "only differences" state: hide matching rows (price is
  // always shown) and any group whose rows are all hidden.
  var applyDiffFilter = function (root) {
    if (!root) return;
    computeDiffs(root);
    var tbody = root.querySelector('[data-eds-cmp-body]');
    if (!tbody) return;

    tbody.querySelectorAll('tr[data-row]').forEach(function (tr) {
      var always = tr.getAttribute('data-row') === 'price';
      tr.hidden = diffOnly && !always && !tr.classList.contains('is-diff');
    });

    tbody.querySelectorAll('.eds-cmp__grouprow').forEach(function (gr) {
      var group = gr.getAttribute('data-eds-cmp-group');
      if (!group) { gr.hidden = false; return; }
      var anyVisible = false;
      tbody.querySelectorAll('tr[data-row][data-eds-cmp-group="' + group + '"]').forEach(function (tr) {
        if (!tr.hidden) anyVisible = true;
      });
      gr.hidden = !anyVisible;
    });
  };

  var buildTable = function (root) {
    if (!root) return;

    var headRow = root.querySelector('[data-eds-cmp-headrow]');
    var tbody = root.querySelector('[data-eds-cmp-body]');

    clearInjected(root);
    updateCounts();

    if (!items.length) {
      setTableState(root, false);
      return;
    }
    setTableState(root, true);

    var queue = items.slice();

    Promise.all(
      queue.map(function (it) {
        return fetchColumn(root, it.handle);
      })
    ).then(function (results) {
      var parser = new DOMParser();
      var missing = [];
      var filled = 0;

      results.forEach(function (res) {
        if (!res.html) {
          missing.push(res.handle);
          return;
        }
        var doc = parser.parseFromString(res.html, 'text/html');
        var col = doc.querySelector('[data-eds-cmp-col]');
        if (!col) {
          missing.push(res.handle);
          return;
        }
        // The column section wraps its cells in a <template>, so the bare
        // th and td survive the parse instead of being dropped.
        var content = col.content || col;
        var head = content.querySelector('[data-eds-cmp-headcell]');
        var cells = content.querySelectorAll('[data-eds-cmp-cell]');

        if (head && headRow) {
          var headNode = document.importNode(head, true);
          headNode.setAttribute('data-eds-cmp-injected', '');
          headRow.appendChild(headNode);
        }

        cells.forEach(function (cell) {
          var rowKey = cell.getAttribute('data-row');
          var tr = tbody && tbody.querySelector('tr[data-row="' + rowKey + '"]');
          if (!tr) return;
          var cellNode = document.importNode(cell, true);
          cellNode.setAttribute('data-eds-cmp-injected', '');
          tr.appendChild(cellNode);
        });

        filled++;
      });

      // A handle that no longer renders (unpublished, deleted) is pruned
      // so the tray and the stored set stay truthful.
      if (missing.length) {
        items = items.filter(function (it) {
          return missing.indexOf(it.handle) === -1;
        });
        persist(items);
        renderTray();
        syncButtons();
      }

      // Pad out to the limit so the set always reads as a full matrix.
      fillEmptySlots(root, headRow, tbody, filled);

      bindColumnRemovers(root);
      setTableState(root, items.length > 0);
      applyDiffFilter(root);
    });
  };

  var activeRoots = function () {
    var roots = pageRoots();
    if (drawerIsOpen()) {
      var dr = drawerRoot();
      if (dr) roots.push(dr);
    }
    return roots;
  };

  // --- Drawer -------------------------------------------------------------

  var drawerBound = false;

  var lockScroll = function () {
    document.documentElement.classList.add('eds-cmp-noscroll');
  };
  var unlockScroll = function () {
    document.documentElement.classList.remove('eds-cmp-noscroll');
  };

  var isOpen = function (d) {
    return !!(d && (d.open || d.hasAttribute('open')));
  };

  var bindDrawer = function (d) {
    if (drawerBound) return;
    drawerBound = true;

    d.querySelectorAll('[data-eds-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        closeDrawer();
      });
    });

    // A click that lands on the dialog element itself is the veil.
    d.addEventListener('click', function (event) {
      if (event.target === d) closeDrawer();
    });

    // Escape closes in both states. A modal dialog also fires its own close,
    // and closeDrawer is a no-op the second time, so the double is harmless.
    d.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        closeDrawer();
      }
    });

    // Native close fires for the close button, the veil, and Escape.
    d.addEventListener('close', function () {
      unlockScroll();
    });
  };

  var openDrawer = function () {
    var d = drawerEl();
    if (!d) {
      // No drawer present, fall back to the page if one is configured.
      if (config.comparePath) window.location.assign(config.comparePath);
      return;
    }
    bindDrawer(d);
    bindDiffToggle();
    bindDrawerClears();
    setColsVar();
    buildTable(drawerRoot());

    // showModal is the path we want: top layer, focus trap, native veil.
    // It can throw (already open, detached, sandbox), so fall back to the
    // open attribute. The dialog carries its own background and a z-index
    // above the tray, so the non modal path still covers the page.
    var opened = false;
    if (typeof d.showModal === 'function' && !d.open) {
      try {
        d.showModal();
        opened = true;
      } catch (e) {
        opened = false;
      }
    }
    if (!opened && !isOpen(d)) {
      d.setAttribute('open', '');
    }
    lockScroll();

    window.requestAnimationFrame(function () {
      var close = d.querySelector('[data-eds-drawer-close]');
      if (close) close.focus();
    });
  };

  var closeDrawer = function () {
    var d = drawerEl();
    if (!d) return;
    if (typeof d.close === 'function' && d.open) {
      try {
        d.close();
      } catch (e) {
        d.removeAttribute('open');
      }
    } else {
      d.removeAttribute('open');
    }
    unlockScroll();
  };

  // --- Wiring -------------------------------------------------------------

  var onChange = function (event) {
    syncButtons();
    renderTray();

    activeRoots().forEach(function (root) {
      buildTable(root);
    });
  };

  // The differences toggle and the drawer clear live in the global drawer, so
  // they are bound against the document and guarded so a re-bind is a no-op.
  var bindDiffToggle = function () {
    document.querySelectorAll('[data-eds-cmp-diff-toggle]').forEach(function (ctrl) {
      if (ctrl.dataset.edsBound) return;
      ctrl.dataset.edsBound = '1';
      ctrl.addEventListener('click', function () {
        diffOnly = !diffOnly;
        ctrl.classList.toggle('is-on', diffOnly);
        ctrl.setAttribute('aria-pressed', diffOnly ? 'true' : 'false');
        activeRoots().forEach(function (root) {
          applyDiffFilter(root);
        });
      });
    });
  };

  var bindDrawerClears = function () {
    document.querySelectorAll('[data-eds-drawer-clear]').forEach(function (btn) {
      if (btn.dataset.edsBound) return;
      btn.dataset.edsBound = '1';
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        clear();
      });
    });
  };

  var initWithin = function (scope) {
    bindButtons(scope);
    bindDiffToggle();
    bindDrawerClears();
    renderTray();
    // Build any compare page table present (page mode). The drawer builds
    // its own table when it opens.
    pageRoots().forEach(function (root) {
      buildTable(root);
    });
  };

  var start = function () {
    document.addEventListener(CHANGE_EVENT, onChange);

    window.addEventListener('storage', function (event) {
      if (event.key !== STORAGE_KEY) return;
      items = load();
      syncButtons();
      renderTray();
      activeRoots().forEach(function (root) {
        buildTable(root);
      });
    });

    document.addEventListener('shopify:section:load', function (event) {
      drawerBound = false;
      trayBound = false;
      initWithin(event.target);
    });
    document.addEventListener('shopify:section:unload', function () {
      syncButtons();
      renderTray();
    });

    initWithin(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Small public surface, handy for cards or selector code that wants to
  // add a drive without a rendered button, or open the drawer directly.
  window.EDSCompare = {
    add: add,
    remove: remove,
    clear: clear,
    has: has,
    isFull: isFull,
    open: openDrawer,
    close: closeDrawer,
    items: function () {
      return items.slice();
    },
    limit: LIMIT
  };
})();
